/**
 * A form generated from a configuration type's tree: one rule per construct, nothing per plugin.
 * FEATURE-20261021-check-config-type-and-fileset-form.
 *
 *   record      its fields, each labelled under the field that holds the record ("Latency · Yellow")
 *   enum        a choice of its cases; the chosen case's fields appear under it
 *   list        of scalars: rows of one value each (the list widget); of records: rows of the
 *               record's fields (the records widget); of anything else: a JSON list
 *   nullable    the value's own control, never required
 *   duration    a number and a unit (the duration widget); saved as its text ("1.5 s")
 *   named       a secret type: a choice among the stored secrets of that type; else a text box
 *   str num bool date time timestamp money any
 *
 * The form's values are flat, keyed by path ("setting.probe.case", "setting.probe.Tcp.host"). The
 * checkpoint vm lays a stored configuration out as those values and assembles it back
 * (src/checkpoint-vm, godev checkpoint/instance/form.go), so the form reads and writes a
 * configuration as the Go runtime does. A field left blank is left out, so the type's default
 * applies; what the type's completion fills in is shown pre-filled.
 */
import type { FormItem, FormItemOption } from "./form-builder.model";
import type { Secret, SecretType } from "../../services/secret-admin-rpc-client";
import { isScalar, type TypeTree, type TypeTreeField } from "./type-tree";
import "./widgets/duration-widget";
import "./widgets/list-widget";
import "./widgets/record-list-widget";

export type TreeFormOpts = {
  types: SecretType[];
  secrets: Secret[];
};

type Placement = {
  required: boolean;
  hasDefault: boolean;
  when?: (values: Record<string, unknown>) => boolean;
};

type Values = Record<string, unknown>;

/** "authToken" as a label: "Auth token". */
export function labelOf(field: string): string {
  const words = field.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function under(parent: string, field: string): string {
  return parent === "" ? field : `${parent} · ${field}`;
}

/** The stored-secret type a named type is, when it is one. */
export function secretTypeOf(name: string, types: SecretType[]): SecretType | undefined {
  return types.find((t) => t.checkpointType !== "" && t.checkpointType === name);
}

function caseKey(path: string): string {
  return `${path}.case`;
}

/** The form items of a value of the type, named under `path`. */
export function itemsFor(tree: TypeTree, path: string, label: string, opts: TreeFormOpts, at: Placement): FormItem[] {
  const optional = at.hasDefault ? " Blank takes the default." : "";
  const base = { name: path, label, required: at.required, showWhen: at.when };
  const fieldItems = (fields: TypeTreeField[], prefix: string, when?: Placement["when"]): FormItem[] =>
    fields.flatMap((f) =>
      itemsFor(f.type, `${prefix}.${f.name}`, under(label, labelOf(f.name)), opts, {
        required: !f.hasDefault,
        hasDefault: f.hasDefault,
        when,
      }),
    );
  switch (tree.kind) {
    case "record":
    case "tuple":
      return fieldItems(tree.fields, path, at.when);
    case "enum": {
      const key = caseKey(path);
      const choices: FormItemOption[] = tree.cases.map((c) => ({ value: c.name, text: labelOf(c.name) }));
      const select: FormItem = {
        ...base,
        name: key,
        type: "select",
        options: at.required ? choices : [{ value: "", text: "(none)" }, ...choices],
        helpText: `One of ${tree.cases.map((c) => labelOf(c.name)).join(", ")}.${optional}`.trim(),
      };
      const opened = tree.cases.flatMap((c) =>
        fieldItems(c.fields, `${path}.${c.name}`, (values) => (at.when ? at.when(values) : true) && values[key] === c.name),
      );
      return [select, ...opened];
    }
    case "nullable":
      return itemsFor(tree.of, path, label, opts, { ...at, required: false });
    case "list":
      if (isScalar(tree.of)) return [{ ...base, type: "widget", widget: "list", config: { of: tree.of.kind }, helpText: `One per row.${optional}` }];
      if (tree.of.kind === "record") return [{ ...base, type: "widget", widget: "records", config: { element: tree.of }, helpText: `One per row.${optional}` }];
      return [{ ...base, type: "json", helpText: `A JSON list.${optional}`, validate: jsonValidator }];
    case "duration":
      return [{ ...base, type: "widget", widget: "duration", helpText: optional.trim() }];
    case "named": {
      const secretType = secretTypeOf(tree.name, opts.types);
      if (!secretType) return [{ ...base, type: "text", helpText: `A ${tree.name}.${optional}` }];
      const stored = opts.secrets.filter((s) => s.kind === secretType.kind);
      return [
        {
          ...base,
          type: "select",
          options: [
            ...(at.required ? [] : [{ value: "", text: "(none)" }]),
            ...stored.map((s) => ({ value: s.name, text: `${s.name} — ${s.description}` })),
          ],
          helpText:
            stored.length === 0
              ? `No ${secretType.kind} secret is stored yet. Add one on the Secrets page, then choose it here.`
              : `A stored ${secretType.kind} secret, by name. Its value is on the Secrets page and is never shown here.`,
        },
      ];
    }
    case "num":
      return [{ ...base, type: "number", helpText: optional.trim() }];
    case "bool":
      return [{ ...base, type: "toggle", required: false }];
    case "date":
      return [{ ...base, type: "text", helpText: `A date, 2026-10-26.${optional}` }];
    case "time":
      return [{ ...base, type: "text", helpText: `A time, 14:30:00.${optional}` }];
    case "timestamp":
      return [{ ...base, type: "text", helpText: `An instant, 2026-10-26T14:30:00Z.${optional}` }];
    case "money":
      return [{ ...base, type: "text", helpText: `An amount with its currency, 12.50 USD.${optional}` }];
    case "any":
      return [{ ...base, type: "json", helpText: `JSON.${optional}`, validate: jsonValidator }];
    case "str":
    default:
      return [{ ...base, type: "text", helpText: optional.trim() }];
  }
}

function jsonValidator(value: unknown) {
  if (typeof value !== "string" || value.trim() === "") return [];
  try {
    JSON.parse(value);
    return [];
  } catch {
    return [{ message: "Not valid JSON.", severity: "error" as const }];
  }
}

/** The names of a record type's top-level fields: what a saved configuration holds for them. */
/**
 * The form item a message's path lands on: "latency.yellow" is the item `${prefix}.latency.yellow`;
 * "probe.port" goes through the probe's chosen case (the values say which) to `${prefix}.probe.Tcp.port`;
 * "seeds[1]" is the seeds box (a list is one item), with "[1]" left as `rest` to prefix the message
 * with. Undefined when the path names nothing the form shows, so the message stays at the top.
 */
export function itemAt(tree: TypeTree, path: string, prefix: string, values: Values): { name: string; rest: string } | undefined {
  const steps = path.split(/\.|(?=\[)/).filter((s) => s !== "");
  let at: TypeTree = tree;
  let name = prefix;
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    while (at.kind === "nullable") at = at.of;
    if (step.startsWith("[")) {
      if (at.kind !== "list") return undefined;
      // "[2]" then "yellow" is "[2].yellow": a field after an index keeps its dot
      return { name, rest: steps.slice(i).reduce((acc, s) => (acc === "" || s.startsWith("[") ? acc + s : `${acc}.${s}`), "") };
    }
    if (at.kind === "record") {
      const field = at.fields.find((f) => f.name === step);
      if (!field) return undefined;
      at = field.type;
      name = `${name}.${step}`;
    } else if (at.kind === "enum") {
      const chosen = String(values[name] ?? "");
      const c = at.cases.find((x) => x.name === chosen);
      const field = c?.fields.find((f) => f.name === step);
      if (!c || !field) return undefined;
      at = field.type;
      name = `${name}.${c.name}.${step}`;
    } else {
      return undefined;
    }
  }
  return { name, rest: "" };
}

export function topLevelFields(tree: TypeTree): string[] {
  return tree.kind === "record" || tree.kind === "tuple" ? tree.fields.map((f) => f.name) : [];
}
